// シフト決定画面（組織版・全社版）のクライアント描画
// - 前へ／次へ、月／週／日表示、リスト／カレンダー表示、入力状況トグル、並び替え、「表示」ボタンを押したときに
//   ページ遷移せずデータ取得API（GET .../lecturer-schedule-decision/data）を呼び、ナビと一覧（またはカレンダー）を描き直す
// - URL は history.pushState で同じクエリに更新するので、再読み込み・ブックマーク・戻るは従来どおり動く
// - 初回表示はサーバー描画（Blade）のまま。JS はリンクのクリックを横取りするだけなので、JS が無効でも従来どおり遷移する
// - 描き直すと未保存の入力内容は消える（ページ遷移していたときと同じ）
//
// 必要な DOM（index.blade.php）:
//   #lecturer-schedule-decision[data-api-url][data-index-url][data-confirm-url][data-query]  画面のルート
//     （全社版は data-shows-organization="1" data-all-organizations="1" を付ける）
//   .js-decision-grid        ナビと一覧／カレンダーを置くグリッド（中身を丸ごと描き直す）
//   .js-decision-notes       リスト表示のときだけ出す注記
//   .js-decision-error       取得に失敗したときのメッセージ
//   #lecturer-schedule-decision-range-form  開始日〜終了日のフォーム（[data-js-show] のボタンで「表示」）
//   a[data-js-nav]           横取りするナビのリンク、thead 内の並び替えリンク（href に sort= を含む）
//   .js-filter-certified / .js-filter-main  表頭のフィルター
//   .btn-toggle-full         全画面表示の切り替えボタン（full_screen.js）
//   a[data-js-lecturer-type] 講師区分の切替タブ（期間を移動したら href を描き直す。クリックはページ遷移のまま）

import { buildUrl, queryFromHref } from './html'
import { renderNav } from './render_nav'
import { renderList } from './render_list'
import { renderCalendar } from './render_calendar'
import { applyFilters, initialFilters, nextState, CERTIFIED_FILTER_STATES, MAIN_LECTURER_FILTER_STATES } from './filters'
import { setUpCalendarPreview } from '../lecturer_schedule_decision_calendar_preview'
import { setUpScrollToUndecided } from '../lecturer_schedule_decision_scroll_to_undecided'
import { setUpFullScreenToggle } from './full_screen'

const ROOT_ID = 'lecturer-schedule-decision'
const RANGE_FORM_ID = 'lecturer-schedule-decision-range-form'
const HISTORY_KEY = 'lecturerScheduleDecision'
const RANGE_FIELDS = ['start_date', 'end_date', 'show_availability', 'display', 'lecturer_type']

const GRID_CLASS_LIST = "grid items-center [grid-template:'nav_.'_max-content_'._.'_2rem_'schedule_schedule'_var(--schedule-h)_/_1fr_4rem] relative js-decision-grid"
const GRID_CLASS_CALENDAR = "grid [grid-template:'nav_.'_max-content_'._.'_2rem_'schedule_schedule'_max-content_/_1fr_4rem] relative js-decision-grid"

/**
 * クエリから並び替えの指定を分離する
 *
 * @param {Record<string, string>} query
 * @returns {{periodQuery: Record<string, string>, sort: {sort: string, direction: string}|null}}
 */
const splitSort = (query) => {
    const { sort, direction, ...periodQuery } = query

    // direction は URL 由来なので asc / desc 以外は asc に丸める（class 属性などに埋め込むため）
    return { periodQuery, sort: sort ? { sort, direction: direction === 'desc' ? 'desc' : 'asc' } : null }
}

/** セッション切れ（Laravel は Accept: application/json の未認証リクエストに 401、CSRF 不一致に 419 を返す） */
const SESSION_EXPIRED_STATUSES = [401, 419]
const SESSION_EXPIRED_MESSAGE = 'セッションが切れました。ページを再読み込みしてログインし直してください。'

/**
 * セッション切れを表すエラーを作る（画面では原因と対処が分かる文言を出す）
 *
 * @returns {Error}
 */
const sessionExpiredError = () => Object.assign(new Error(SESSION_EXPIRED_MESSAGE), { sessionExpired: true })

/**
 * 取得に失敗したときに画面へ出す文言
 *
 * @param {Error} error
 * @returns {string}
 */
const toErrorMessage = (error) => (error.sessionExpired
    ? SESSION_EXPIRED_MESSAGE
    : `データの取得に失敗しました（${error.message}）。ページを再読み込みしてください。`)

/**
 * データ取得APIを呼ぶ
 *
 * @param {string} apiUrl
 * @param {Record<string, string>} query
 * @param {AbortSignal|undefined} signal 取得中に別の操作が来たときに中断するためのシグナル
 * @returns {Promise<object>}
 */
const fetchData = async (apiUrl, query, signal = undefined) => {
    const response = await fetch(buildUrl(apiUrl, query), {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        signal,
    })
    if (SESSION_EXPIRED_STATUSES.includes(response.status)) {
        throw sessionExpiredError()
    }
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
    }
    // ログイン画面の HTML が 200 で返ってきた場合に、JSON の構文エラーをそのまま出さない
    if (!(response.headers.get('content-type') ?? '').includes('application/json')) {
        throw new Error('応答の形式が不正です')
    }

    return response.json()
}

/**
 * 開始日〜終了日のフォームの値を、表示中の期間・表示オプションに合わせる
 *
 * @param {HTMLFormElement} form
 * @param {object} data
 * @returns {void}
 */
const syncRangeForm = (form, data) => {
    form.elements.start_date.value = data.period.start_date
    form.elements.end_date.value = data.period.end_date

    const setHidden = (name, value) => {
        const existing = form.querySelector(`input[type="hidden"][name="${name}"]`)
        if (value === null) {
            existing?.remove()
            return
        }
        const input = existing || Object.assign(document.createElement('input'), { type: 'hidden', name })
        input.value = value
        if (!existing) {
            form.querySelector('[data-js-show]').insertAdjacentElement('beforebegin', input)
        }
    }
    setHidden('show_availability', data.display.shows_availability ? '1' : null)
    setHidden('display', data.display.mode === 'calendar' ? 'calendar' : null)
    setHidden('lecturer_type', data.display.lecturer_type === 'designer' ? 'designer' : null)
}

/**
 * 講師区分（マーケター／デザイナー）の切替タブのリンクを、表示中の期間・表示オプションに合わせる
 * （タブはページ遷移で切り替えるため data-js-nav を付けていない。期間を移動したあとも同じ期間で切り替わるようにする）
 *
 * @param {HTMLElement} root
 * @param {object} data
 * @param {string} indexUrl
 * @returns {void}
 */
const syncLecturerTypeTabs = (root, data, indexUrl) => {
    root.querySelectorAll('a[data-js-lecturer-type]').forEach((link) => {
        const query = link.dataset.jsLecturerType === 'designer' ? data.display.designer_type_query : data.display.marketer_type_query
        link.href = buildUrl(indexUrl, query)
    })
}

const setUpLecturerScheduleDecision = () => {
    const root = document.getElementById(ROOT_ID)
    const rangeForm = document.getElementById(RANGE_FORM_ID)
    if (!root || !rangeForm) {
        return
    }

    const grid = root.querySelector('.js-decision-grid')
    const notes = root.querySelector('.js-decision-notes')
    const errorBox = root.querySelector('.js-decision-error')
    const context = {
        apiUrl: root.dataset.apiUrl,
        indexUrl: root.dataset.indexUrl,
        confirmUrl: root.dataset.confirmUrl,
        csrf: document.querySelector('meta[name="csrf-token"]')?.content ?? '',
        // 初回表示の並び順は URL の sort / direction から引き継ぐ
        sort: splitSort(queryFromHref(window.location.href)).sort,
        // 全社版: 企業名列を出し、注記を全組織向けの文言にする
        showsOrganization: root.dataset.showsOrganization === '1',
        isAllOrganizations: root.dataset.allOrganizations === '1',
    }
    const state = {
        periodQuery: JSON.parse(root.dataset.query || '{}'),
        filters: initialFilters(),
        // 取得の連番。最後に始めた取得だけを画面に反映する（取得中に押した操作を捨てない）
        requestSeq: 0,
        // 取得中のリクエストを中断するための AbortController
        pendingRequest: null,
    }

    /**
     * 取得したデータで画面を描き直す
     *
     * @param {object} data
     * @returns {void}
     */
    const render = (data) => {
        const isCalendar = data.display.mode === 'calendar'
        grid.className = isCalendar ? GRID_CLASS_CALENDAR : GRID_CLASS_LIST
        grid.innerHTML = renderNav(data, context) + (isCalendar ? renderCalendar(data, context) : renderList(data, context))
        if (notes) {
            notes.hidden = isCalendar
        }
        syncRangeForm(rangeForm, data)
        // 描き直した要素に、割り当てプレビューと「未確定のコースを表示」を付け直す
        setUpCalendarPreview()
        setUpScrollToUndecided()
        applyFilters(grid, state.filters)
    }

    /**
     * 指定したクエリのデータを取得して描き直す
     *
     * @param {Record<string, string>} query 期間・表示オプション（sort / direction を含んでもよい）
     * @param {{pushHistory: boolean}} options
     * @returns {Promise<void>}
     */
    const load = async (query, { pushHistory } = { pushHistory: true }) => {
        const { periodQuery, sort } = splitSort(query)
        // 取得中に別の操作が来たら、前の取得は中断して後から押したほうを優先する
        state.pendingRequest?.abort()
        const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null
        state.pendingRequest = controller
        const seq = ++state.requestSeq
        grid.setAttribute('aria-busy', 'true')
        grid.style.opacity = '0.5'
        errorBox.hidden = true

        try {
            const data = await fetchData(context.apiUrl, { ...periodQuery, ...(sort ?? {}) }, controller?.signal)
            if (seq !== state.requestSeq) {
                // 後から始まった取得があるので、この結果は使わない
                return
            }
            state.periodQuery = data.period.query
            context.sort = sort
            render(data)
            syncLecturerTypeTabs(root, data, context.indexUrl)

            const url = buildUrl(context.indexUrl, { ...data.period.query, ...(sort ?? {}) })
            const historyState = { [HISTORY_KEY]: true, query: { ...data.period.query, ...(sort ?? {}) } }
            if (pushHistory) {
                window.history.pushState(historyState, '', url)
            } else {
                window.history.replaceState(historyState, '', url)
            }
        } catch (error) {
            // 中断した取得と、後から始まった取得に追い越された取得のエラーは出さない
            if (error.name !== 'AbortError' && seq === state.requestSeq) {
                errorBox.textContent = toErrorMessage(error)
                errorBox.hidden = false
            }
        } finally {
            if (seq === state.requestSeq) {
                state.pendingRequest = null
                grid.removeAttribute('aria-busy')
                grid.style.opacity = ''
            }
        }
    }

    // ナビのリンク・並び替えリンク・表頭フィルターのクリック（描き直しても効くように root で受ける）
    root.addEventListener('click', (event) => {
        const navLink = event.target.closest('a[data-js-nav]')
        if (navLink) {
            event.preventDefault()
            load(queryFromHref(navLink.href))
            return
        }
        const sortLink = event.target.closest('thead a[href*="sort="]')
        if (sortLink) {
            event.preventDefault()
            load({ ...state.periodQuery, ...splitSort(queryFromHref(sortLink.href)).sort })
            return
        }
        if (event.target.closest('.js-filter-certified')) {
            state.filters.certified = nextState(CERTIFIED_FILTER_STATES, state.filters.certified)
            applyFilters(grid, state.filters)
            return
        }
        if (event.target.closest('.js-filter-main')) {
            state.filters.main = nextState(MAIN_LECTURER_FILTER_STATES, state.filters.main)
            applyFilters(grid, state.filters)
        }
    })

    // 「表示」ボタン: 開始日〜終了日（と表示オプション）でデータを取得する。「DLする」はそのまま送信する
    rangeForm.addEventListener('submit', (event) => {
        if (!event.submitter || !event.submitter.hasAttribute('data-js-show')) {
            return
        }
        event.preventDefault()
        const formData = new FormData(rangeForm)
        const query = Object.fromEntries(RANGE_FIELDS.filter((field) => formData.get(field)).map((field) => [field, formData.get(field)]))
        load(query)
    })

    // 戻る／進む
    window.addEventListener('popstate', (event) => {
        if (event.state && event.state[HISTORY_KEY]) {
            load(event.state.query, { pushHistory: false })
        }
    })
    window.history.replaceState({ [HISTORY_KEY]: true, query: { ...state.periodQuery, ...(context.sort ?? {}) } }, '', window.location.href)

    // 全画面表示の切り替え（ボタンは描き直しの対象外なので一度だけ設定する）
    setUpFullScreenToggle(root)

    // サーバー描画の表頭フィルターのインジケーターも初期状態にそろえる
    applyFilters(grid, state.filters)
}

document.addEventListener('DOMContentLoaded', setUpLecturerScheduleDecision)
